import React, { useState, CSSProperties, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import { CaseDetail } from '../../../../models/CaseDetail'
import { colors } from '../../../../theme/palette'
import { Button } from '../../../ui/Button/Button'
import { Typography } from '../../../ui/Typography/Typography'
import { cardProps, control, tooltip } from '../../../../theme/reuseables'
import { FormControl } from '../../../ui/FormElememts/FormControl'
import { FormTextArea } from '../../../modules/FormElements/FormInput'
import { createCaseNote, CreateCaseNoteType } from '../../../../api/salary'
import { L } from '../../../providers/Lokalise'
import { Help } from '../../../../assets/icons'
import { Tooltip } from '../../../ui/Tooltip/Tooltip'
import { useErrorDialog } from '../../../../hooks/useErrorDialog'
import { ErrorDisplay } from '../Contact/ContactDialog'

type CaseNotesProps = {
    caseDetails: CaseDetail
    onUpdated?: () => void
    onCancel?: () => void
    isOpen?: boolean
}

const useStyles = createUseStyles({
    control: {
        ...control,
    },
    card: {
        ...cardProps.card,
    },
    title: {
        ...cardProps.title,
    },
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100%',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        '&.visible': {
            transform: 'translateX(0)',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

type RegistrationFormFields = {
    reminderAarsagFritekst: string
}

const CaseNoteCreateView: React.FC<CaseNotesProps> = ({
    caseDetails,
    onUpdated,
    onCancel,
    isOpen = false,
}): JSX.Element => {
    const classes = useStyles()
    const [isLoading, setIsLoading] = useState(false)
    const { register, handleSubmit, formState, reset } =
        useForm<RegistrationFormFields>()
    const { showError, ErrorDialogComponent } = useErrorDialog() as any
    const { isDirty, dirtyFields } = formState

    const resetForm = (): void => {
        reset()
    }

    const onSubmit = async (data: RegistrationFormFields): Promise<void> => {
        if (!isDirty || !dirtyFields.reminderAarsagFritekst) {
            return
        }

        setIsLoading(true)

        const fields: CreateCaseNoteType = {
            caseId: caseDetails.caseId,
            caseRegTime: caseDetails.caseRegistrationTime,
            message: data.reminderAarsagFritekst,
        }

        const response = await createCaseNote(fields)

        if (response.error) {
            showError(<ErrorDisplay errors={response.error.errors} />)
            setIsLoading(false)
            return
        }

        if (response) {
            resetForm()
            onUpdated?.()
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (isOpen) {
            resetForm()
        }
    }, [isOpen])

    return (
        <>
            <div
                className={`${classes.contentWrapper} ${
                    isOpen ? 'visible' : 'visible'
                }`}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography
                        tag="h2"
                        size="XL"
                        align="center"
                        weight="bold"
                        style={{ margin: '20px 0 10px' }}
                    >
                        {L('LØN_OPRET_NOTAT_DIALOG_TITEL')}
                    </Typography>
                    <FormControl>
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Tooltip
                                content={L(
                                    'LØN_SAGS_NOTE_KORT_TILFØJ_NOTAT_TOOLTIP'
                                )}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontStyle: 'italic',
                                        justifyContent: 'flex-end',
                                        width: 'fit-content',
                                        padding: '.5rem 0',
                                    }}
                                >
                                    <Typography
                                        size="XXS"
                                        color={colors.labelColor}
                                        style={{
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        {L(
                                            'LØN_SAGS_NOTE_KORT_HVEM_KAN_SE_NOTAT_LABEL'
                                        )}
                                    </Typography>

                                    <div
                                        style={{
                                            width: '1rem',
                                            border: `1px solid ${colors.green}`,
                                            height: '1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginLeft: '5px',
                                            borderRadius: '50%',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Help
                                            width="1rem"
                                            fill={colors.green}
                                        />
                                    </div>
                                </div>
                            </Tooltip>
                        </div>
                        <FormTextArea
                            label={`${L(
                                'LØN_SAGS_NOTE_KORT_TILFØJ_NOTAT_LABEL'
                            )}*`}
                            register={register}
                            name="reminderAarsagFritekst"
                            maxCount={3000}
                        />
                    </FormControl>
                </form>
            </div>
            <div className={classes.bottom}>
                <Button onClick={onCancel} type="secondary" size="large">
                    {L('LØN_SAGS_NOTE_KORT_ANNULLER_KNAP')}
                </Button>
                <Button
                    buttonType="submit"
                    type="primary"
                    size="large"
                    loading={isLoading}
                    onClick={handleSubmit(onSubmit)}
                >
                    {L('LØN_SAGS_NOTE_KORT_OPRET_KNAP')}
                </Button>
            </div>
            <ErrorDialogComponent />
        </>
    )
}

export default CaseNoteCreateView
